import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { supabase } from '../../lib/supabase';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ReferralLinksCardProps {
  userId: number;
  className?: string;
}

interface UserInfo {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export const ReferralLinksCard: React.FC<ReferralLinksCardProps> = ({ userId: rawUserId, className: _className = '' }) => {
  // CRITICAL FIX: Ensure userId is always a number to prevent type mismatch with database IDs
  const userId = typeof rawUserId === 'string' ? parseInt(rawUserId, 10) : Number(rawUserId);

  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState<string>('');
  const [hasApprovedPurchase, setHasApprovedPurchase] = useState<boolean>(false);

  useEffect(() => {
    loadUserInfo();
    checkPurchaseStatus();
  }, [userId]);

  const loadUserInfo = async () => {
    try {
      // First try localStorage (already stored during login, avoids RLS issues)
      const storedUser = localStorage.getItem('aureus_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const dbUser = parsed.database_user || parsed;
        if (dbUser && (dbUser.id === userId || parseInt(dbUser.id) === userId)) {
          setUser({
            id: userId,
            username: dbUser.username || '',
            email: dbUser.email || '',
            first_name: dbUser.first_name,
            last_name: dbUser.last_name,
          });
          setLoading(false);
          return;
        }
      }

      // Fallback: query Supabase (may fail due to RLS)
      const { data: userData, error } = await supabase
        .from('users')
        .select('id, username, email, first_name, last_name')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUser(userData);
    } catch (error) {
      console.error('Error loading user info:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/referral-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'check_purchase_status' })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)
      setHasApprovedPurchase(result.data?.hasPurchased || false)
    } catch (error) {
      console.error('Error checking purchase status:', error);
      setHasApprovedPurchase(false);
    }
  };

  const getReferralUsername = (user: UserInfo) => {
    // Use exact database username without any modification
    return user?.username || `user${user.id}`;
  };

  const generateReferralLinks = (user: UserInfo) => {
    const username = getReferralUsername(user);

    return {
      customLandingPage: `https://aureus.africa/${username}`
    };
  };

  const copyToClipboard = async (link: string, linkType: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(linkType);
      setTimeout(() => setCopiedLink(''), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(linkType);
      setTimeout(() => setCopiedLink(''), 2000);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
        <div className="text-3xl mb-2">🔗</div>
        <h3 className="text-white text-lg font-semibold mb-2">Referral Links</h3>
        <p className="text-white text-sm">Loading your referral links...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
        <div className="text-3xl mb-2">❌</div>
        <h3 className="text-white text-lg font-semibold mb-2">Error</h3>
        <p className="text-white text-sm">Unable to load referral links</p>
      </div>
    );
  }

  // If user hasn't purchased at least one share, show locked state
  if (!hasApprovedPurchase) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h3 className="text-white text-lg font-semibold mb-2">Referral Link Locked</h3>
        <p className="text-white text-sm mb-4">
          Purchase at least 1 share to unlock your personal referral link and start referring friends.
        </p>
        <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center space-x-2 text-yellow-400">
            <span className="text-lg">💡</span>
            <span className="text-sm font-medium">Why purchase first?</span>
          </div>
          <p className="text-gray-300 text-sm mt-2">
            We require shareholders to have skin in the game before referring others. This ensures our community consists of committed shareholders.
          </p>
        </div>
        <button
          onClick={() => {
            const purchaseSection = document.querySelector('[data-section="purchase"]');
            if (purchaseSection) {
              purchaseSection.scrollIntoView({ behavior: 'smooth' });
            } else {
              window.location.href = '/dashboard?section=purchase';
            }
          }}
          className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg text-sm font-medium transition-colors w-full"
        >
          🛒 Purchase Your First Share
        </button>
      </div>
    );
  }

  const links = generateReferralLinks(user);
  const username = getReferralUsername(user);

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="text-center mb-4">
        <div className="text-3xl mb-2">🔗</div>
        <h3 className="text-white text-lg font-semibold mb-2">Referral Links</h3>
        <p className="text-white text-sm mb-2">
          Share your links to grow your network
        </p>
        <div className="bg-gray-700 rounded-lg p-2 mb-4">
          <span className="text-yellow-400 font-mono text-sm">@{username}</span>
        </div>
      </div>

      <div className="space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Primary Custom Landing Page */}
        <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-4" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-white text-lg">🎯</span>
              <span className="text-white text-sm font-semibold">Your Personal Landing Page</span>
            </div>
            <button
              onClick={() => copyToClipboard(links.customLandingPage, 'landing')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors w-full sm:w-auto ${
                copiedLink === 'landing'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-yellow-600 hover:bg-gray-100'
              }`}
            >
              {copiedLink === 'landing' ? '✓ Copied!' : 'Copy Link'}
            </button>
          </div>
          <div className="text-white text-sm font-mono break-all bg-black/20 p-3 rounded mb-3" style={{ wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%' }}>
            {links.customLandingPage}
          </div>
          <div className="text-white text-sm" style={{ wordWrap: 'break-word' }}>
            ✨ Professional landing page with your profile, company info, calculator, and auto-registration
          </div>
        </div>


      </div>

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-gray-600" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="grid grid-cols-2 gap-2" style={{ width: '100%', maxWidth: '100%' }}>
          <button
            onClick={() => window.open(links.customLandingPage, '_blank')}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-2 rounded text-sm font-medium transition-colors"
            style={{ width: '100%', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            👁️ Preview
          </button>
          <button
            onClick={() => {
              const marketingSection = document.querySelector('[data-section="marketing"]');
              if (marketingSection) {
                marketingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-2 rounded text-sm font-medium transition-colors"
            style={{ width: '100%', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            📱 Marketing
          </button>
        </div>
      </div>
    </div>
  );
};
